<template>
  <div class="billing-start-shell sba-slide-in">
    <div class="billing-start-card">
      <div class="billing-start-main" :class="{ 'has-error': error }">
        <div class="billing-start-copy">
          <span>Provider Billing</span>
          <h6>Start Batch</h6>
          <p>Create a new Claims batch.</p>
        </div>

        <div class="billing-start-context">
          <i class="material-icons">business</i>
          <div>
            <small>Provider Account</small>
            <strong>{{ providerName }}</strong>
          </div>
        </div>
      </div>

      <div class="billing-start-form">
        <section class="billing-start-section">
          <div class="billing-start-section-head">
            <h6>Period</h6>
            <i class="material-icons billing-start-check" v-if="selectedDurationMonth && selectedDurationYear">check_circle</i>
          </div>
          <div class="billing-start-grid">
            <div class="input-field">
              <FilterSimpleDiv inputId="filterBillingDurationMonth" fieldName="Select Month" :data="filterDurationMonthData"
                :search="false" :appendDropdownToBody="true" :forceDropdownDown="true"
                dropdownClass="billing-start-dropdown" @selected="handlePick" />
            </div>
            <div class="input-field">
              <FilterSimpleDiv inputId="filterBillingDurationYear" fieldName="Select Year" :data="filterDurationYearData"
                :search="false" :appendDropdownToBody="true" :forceDropdownDown="true"
                dropdownClass="billing-start-dropdown" @selected="handlePick" />
            </div>
          </div>
        </section>

        <section class="billing-start-section">
          <div class="billing-start-section-head">
            <h6>Scheme</h6>
            <i class="material-icons billing-start-check" v-if="selectedScheme">check_circle</i>
          </div>
          <div class="billing-start-field">
            <div class="input-field">
              <FilterSimpleDiv inputId="filterBillingScheme" fieldName="Select Scheme" :data="filterSchemeData"
                :search="false" :appendDropdownToBody="true" :forceDropdownDown="true"
                dropdownClass="billing-start-dropdown" @selected="handlePick" />
            </div>
          </div>
        </section>
      </div>

      <div class="billing-start-error" v-if="error">
        <i class="material-icons">error_outline</i>
        <span>{{ error }}</span>
      </div>

      <div class="billing-start-submit-row">
        <button type="button" class="billing-start-cancel" :class="{ disabled: isSubmitting }" @click.prevent="handleCancel">
          <i class="material-icons">close</i>
          <span>Cancel</span>
        </button>
        <button type="button" class="billing-start-submit" :class="{ disabled: !canSubmit }" @click.prevent="handleConfirmOpen">
          <span>{{ isSubmitting ? "Starting..." : "Start Batch" }}</span>
          <i class="material-icons">{{ isSubmitting ? "hourglass_empty" : "chevron_right" }}</i>
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div class="billing-start-confirm-overlay" @click.self="handleConfirmClose" v-if="isConfirmOpen">
      <section class="billing-start-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="billing-start-confirm-title">
        <div class="billing-start-confirm-content">
          <span>Confirm Batch</span>
          <h4 id="billing-start-confirm-title">Create Billing Batch?</h4>
          <p>
            You are about to create a new Claims batch for <b>{{ confirmationScheme }}</b> enrollees for
            {{ confirmationMonth }}, {{ confirmationYear }}. Kindly note that only enrollees from
            {{ confirmationScheme }} Scheme who attended your facility in
            {{ confirmationMonth }} {{ confirmationYear }} can be added to this batch.
          </p>
        </div>
        <div class="billing-start-confirm-footer">
          <button type="button" class="billing-start-confirm-btn is-cancel" :class="{ disabled: isSubmitting }" @click.prevent="handleConfirmClose">
            Cancel
          </button>
          <button type="button" class="billing-start-confirm-btn is-create" :class="{ disabled: isSubmitting }" @click.prevent="handleSubmit">
            <span>{{ isSubmitting ? "Creating..." : "Create" }}</span>
            <i class="material-icons" v-if="!isSubmitting">check_circle</i>
          </button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import FilterSimpleDiv from "@/components/search/FilterSimpleDiv.vue";
import { getCachedServerDate } from "@/composables/requestOptimization";
import { claimCap } from "@/laravel/config";

export default {
  name: "StartBillingBatchComponent",
  components: { FilterSimpleDiv },
  emits: ["started", "cancel"],
  setup(props, context) {
    const isSubmitting = ref(false);
    const error = ref(null);
    const responseDurationYearDefault = ref(null);
    const responseDurationMonthDefault = ref(null);
    const responseSchemeDefault = ref(null);
    const selectedItem = ref(null);
    const selectedDurationMonth = ref(null);
    const selectedDurationYear = ref(null);
    const selectedScheme = ref(null);
    const isConfirmOpen = ref(false);
    const claimYearCap = Math.max(1, Number(claimCap) || 3);

    // store
    const store = useStore();

    const providerName = computed(() => {
      return store.state.user?.account?.name || store.state.user?.name || "Current provider";
    });

    const filterDurationMonthData = computed(() => {
      let val = null;

      if (responseDurationMonthDefault.value?.key == "filterBillingDurationMonth") {
        const valPlus = responseDurationMonthDefault.value.value.reduce((newArr, x) => {
          newArr[x.period] = null;
          return newArr;
        }, {});
        val = { ...val, ...valPlus };
      }

      return val;
    });

    const filterDurationYearData = computed(() => {
      let val = null;

      if (responseDurationYearDefault.value?.key == "filterBillingDurationYear") {
        const valPlus = responseDurationYearDefault.value.value.reduce((newArr, x) => {
          newArr[x.period] = null;
          return newArr;
        }, {});
        val = { ...val, ...valPlus };
      }

      return val;
    });

    const filterSchemeData = computed(() => {
      let val = null;

      if (responseSchemeDefault.value?.key == "filterBillingScheme") {
        const valPlus = responseSchemeDefault.value.value.reduce((newArr, x) => {
          newArr[x.name] = null;
          return newArr;
        }, {});
        val = { ...val, ...valPlus };
      }

      return val;
    });

    const canSubmit = computed(() => {
      return Boolean(
        selectedDurationMonth.value &&
        selectedDurationYear.value &&
        selectedScheme.value &&
        !isSubmitting.value
      );
    });

    const confirmationScheme = computed(() => selectedScheme.value?.name || "the selected scheme");
    const confirmationMonth = computed(() => selectedDurationMonth.value?.name || "the selected month");
    const confirmationYear = computed(() => selectedDurationYear.value?.name || "the selected year");
    const isManualScheme = (scheme) => {
      return scheme?.is_manual === true || scheme?.is_manual === 1 || scheme?.is_manual === "1";
    };

    onMounted(() => {
      responseDurationMonthDefault.value = {
        key: "filterBillingDurationMonth",
        value: [
          { period: "January", month: 1 },
          { period: "February", month: 2 },
          { period: "March", month: 3 },
          { period: "April", month: 4 },
          { period: "May", month: 5 },
          { period: "June", month: 6 },
          { period: "July", month: 7 },
          { period: "August", month: 8 },
          { period: "September", month: 9 },
          { period: "October", month: 10 },
          { period: "November", month: 11 },
          { period: "December", month: 12 },
        ],
      };
    });

    onMounted(() => {
      const manualSchemes = (store.state.user?.schemes || []).filter(isManualScheme);

      responseSchemeDefault.value = {
        key: "filterBillingScheme",
        value: manualSchemes,
      };

      if (!manualSchemes.length) {
        error.value = "No manual billing scheme is enabled for this provider account.";
      }
    });

    onMounted(() => {
      responseDurationYearDefault.value = {
        key: "filterBillingDurationYear",
        value: [],
      };

      (async () => {
        const { res, err } = await getCachedServerDate(getItems);

        if (res.value) {
          responseDurationYearDefault.value = {
            key: "filterBillingDurationYear",
            value: Array.from({ length: claimYearCap }, (_, index) => {
              const year = res.value.data.current_year - claimYearCap + index + 1;
              return { period: year, year: year };
            }),
          };
        } else {
          error.value = err.value || "Unable to load billing years.";
        }
      })();
    });

    const handlePick = (val) => {
      selectedItem.value = val;
    };

    const handleCancel = () => {
      if (!isSubmitting.value) {
        context.emit("cancel");
      }
    };

    const handleConfirmOpen = () => {
      error.value = null;

      if (!store.state.user?.account?.id) {
        error.value = "This sign-in is not linked to a provider account.";
        return;
      }

      if (!selectedDurationMonth.value || !selectedDurationYear.value || !selectedScheme.value) {
        error.value = "Select a billing month, year, and scheme.";
        return;
      }

      isConfirmOpen.value = true;
    };

    const handleConfirmClose = () => {
      if (!isSubmitting.value) {
        isConfirmOpen.value = false;
      }
    };

    const providerStartErrorMessage = (err, errorType) => {
      if (errorType === "forbidden") {
        return err || "This provider account cannot start billing batches.";
      }

      if (errorType === "validation") {
        return err || "Check the billing period and scheme, then try again.";
      }

      if (errorType === "network") {
        return "Unable to reach easyHMO. Please check your connection and try again.";
      }

      return err || "Unable to start this billing batch right now.";
    };

    const handleSubmit = async () => {
      error.value = null;

      if (!store.state.user?.account?.id) {
        error.value = "This sign-in is not linked to a provider account.";
        return;
      }

      if (!selectedDurationMonth.value || !selectedDurationYear.value || !selectedScheme.value) {
        error.value = "Select a billing month, year, and scheme.";
        return;
      }

      isSubmitting.value = true;

      const { res, err, errorType } = await postItem("provider/billing/clusters/start", {
        month: selectedDurationMonth.value.search,
        year: selectedDurationYear.value.search,
        scheme_id: selectedScheme.value.search,
      });

      if (res.value?.data) {
        isConfirmOpen.value = false;
        context.emit("started", res.value.data);
      } else {
        error.value = providerStartErrorMessage(err.value, errorType.value);
      }

      isSubmitting.value = false;
    };

    watchEffect(() => {
      if (!selectedItem.value) {
        return;
      }

      if (selectedItem.value.id == "filterBillingDurationMonth") {
        if (!selectedItem.value.value) {
          selectedDurationMonth.value = null;
        } else {
          responseDurationMonthDefault.value.value.forEach((x) => {
            if (x.period == selectedItem.value.value) {
              selectedDurationMonth.value = { name: x.period, search: x.month };
            }
          });
        }
      } else if (selectedItem.value.id == "filterBillingDurationYear") {
        if (!selectedItem.value.value) {
          selectedDurationYear.value = null;
        } else {
          responseDurationYearDefault.value.value.forEach((x) => {
            if (x.period == selectedItem.value.value) {
              selectedDurationYear.value = { name: x.period, search: x.year };
            }
          });
        }
      } else if (selectedItem.value.id == "filterBillingScheme") {
        if (!selectedItem.value.value) {
          selectedScheme.value = null;
        } else {
          responseSchemeDefault.value.value.forEach((x) => {
            if (x.name == selectedItem.value.value) {
              selectedScheme.value = { name: x.name, search: x.id };
            }
          });
        }
      }
    });

    return {
      canSubmit,
      confirmationMonth,
      confirmationScheme,
      confirmationYear,
      error,
      filterDurationMonthData,
      filterDurationYearData,
      filterSchemeData,
      handleCancel,
      handleConfirmClose,
      handleConfirmOpen,
      handlePick,
      handleSubmit,
      isConfirmOpen,
      isSubmitting,
      providerName,
      selectedDurationMonth,
      selectedDurationYear,
      selectedScheme,
    };
  },
};
</script>

<style scoped>
.billing-start-shell {
  margin-top: 16px;
}

.billing-start-card {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow:
    -8px 0 16px -15px rgba(38, 50, 56, 0.45),
    8px 0 16px -15px rgba(38, 50, 56, 0.45),
    0 10px 18px -20px rgba(38, 50, 56, 0.35);
  overflow: hidden;
}

.billing-start-main {
  align-items: center;
  background: #ffffff;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(220px, 1fr) minmax(220px, 0.6fr);
  padding: 24px;
}

.billing-start-main.has-error {
  background: #fff8f8;
}

.billing-start-copy span {
  color: #009688;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.billing-start-copy h6,
.billing-start-section-head h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.billing-start-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 8px 0 0;
}

.billing-start-context {
  align-items: center;
  background: #fafafa;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 14px 16px;
}

.billing-start-context i {
  color: #009688;
  font-size: 24px;
}

.billing-start-context small {
  color: #607d8b;
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.billing-start-context strong {
  color: #263238;
  display: block;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
}

.billing-start-form {
  border-top: 1px solid #e1e8eb;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 0.55fr);
}

.billing-start-section {
  border-right: 1px solid #e1e8eb;
  padding: 20px 24px 12px;
}

.billing-start-section:last-child {
  border-right: 0;
}

.billing-start-section-head {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.billing-start-check {
  color: #00897b;
  font-size: 19px;
  margin-left: auto;
}

.billing-start-field,
.billing-start-grid {
  padding-left: 0;
}

.billing-start-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.72fr);
}

.billing-start-error {
  align-items: center;
  background: #fff3e0;
  border-top: 1px solid #ffe0b2;
  color: #4e342e;
  display: flex;
  gap: 10px;
  padding: 12px 24px;
}

.billing-start-error i {
  color: #ef6c00;
  font-size: 20px;
}

.billing-start-submit-row {
  align-items: center;
  background: #f8fafb;
  border-top: 1px solid #e1e8eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 14px 24px;
}

.billing-start-cancel,
.billing-start-submit {
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 10px;
  justify-content: center;
  min-height: 42px;
  padding: 0 18px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, opacity 0.18s ease;
}

.billing-start-cancel {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  color: #455a64;
}

.billing-start-cancel:hover {
  background: #eceff1;
}

.billing-start-submit {
  background: #00897b;
  border: 1px solid #009688;
  color: #ffffff;
  box-shadow: 0 10px 20px -18px rgba(38, 50, 56, 0.8);
}

.billing-start-submit:hover {
  background: #00796b;
  border-color: #00796b;
  box-shadow: 0 14px 24px -20px rgba(38, 50, 56, 0.85);
  color: #ffffff;
}

.billing-start-cancel.disabled,
.billing-start-submit.disabled {
  cursor: default;
  opacity: 0.52;
  pointer-events: none;
}

.billing-start-cancel i,
.billing-start-submit i {
  font-size: 18px;
  line-height: 18px;
}

.billing-start-confirm-overlay {
  align-items: center;
  background: rgba(38, 50, 56, 0.42);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1600;
}

.billing-start-confirm-modal {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 24px 52px -36px rgba(38, 50, 56, 0.95);
  max-width: 560px;
  overflow: hidden;
  width: min(560px, 100%);
}

.billing-start-confirm-content {
  padding: 26px 28px 22px;
}

.billing-start-confirm-content > span {
  color: #009688;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.billing-start-confirm-content h4 {
  color: #263238;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 12px;
}

.billing-start-confirm-content p {
  color: #455a64;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
}

.billing-start-confirm-content b {
  color: #263238;
  font-weight: 800;
}

.billing-start-confirm-footer {
  align-items: center;
  background: #f8fafb;
  border-top: 1px solid #e1e8eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 14px 18px;
}

.billing-start-confirm-btn {
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 9px;
  justify-content: center;
  min-height: 42px;
  min-width: 112px;
  padding: 0 18px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, opacity 0.18s ease;
}

.billing-start-confirm-btn.is-cancel {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  color: #455a64;
}

.billing-start-confirm-btn.is-cancel:hover,
.billing-start-confirm-btn.is-cancel:focus {
  background: #eceff1;
}

.billing-start-confirm-btn.is-create {
  background: #00897b;
  border: 1px solid #00897b;
  color: #ffffff;
}

.billing-start-confirm-btn.is-create:hover,
.billing-start-confirm-btn.is-create:focus {
  background: #00695c;
  border-color: #00695c;
}

.billing-start-confirm-btn.disabled {
  cursor: default;
  opacity: 0.52;
  pointer-events: none;
}

.billing-start-confirm-btn i {
  font-size: 18px;
  line-height: 18px;
}

:global(.billing-start-dropdown.autocomplete-content.dropdown-content) {
  max-height: 320px;
  overflow-y: auto;
  z-index: 1200;
}

:global(.billing-start-dropdown.autocomplete-content.dropdown-content li > span) {
  color: #00897b;
  font-size: 1rem;
}

@media only screen and (max-width: 900px) {
  .billing-start-main,
  .billing-start-form,
  .billing-start-grid {
    grid-template-columns: 1fr;
  }

  .billing-start-section {
    border-right: 0;
    border-bottom: 1px solid #e1e8eb;
  }

  .billing-start-section:last-child {
    border-bottom: 0;
  }
}

@media only screen and (max-width: 700px) {
  .billing-start-main,
  .billing-start-section {
    padding: 18px;
  }

  .billing-start-submit-row {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 14px 18px;
  }

  .billing-start-cancel,
  .billing-start-submit {
    width: 100%;
  }

  .billing-start-confirm-overlay {
    align-items: flex-end;
    padding: 14px;
  }

  .billing-start-confirm-content {
    padding: 22px 20px 18px;
  }

  .billing-start-confirm-footer {
    align-items: stretch;
    flex-direction: column-reverse;
    padding: 14px;
  }

  .billing-start-confirm-btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .billing-start-card,
  .billing-start-confirm-modal {
    animation: none;
  }
}
</style>
